import WebAuth from 'auth0-js/src/web-auth/index';
import jwtDecode from 'jwt-decode';
import getAPI from './api';
import { parseQueryString, toQueryString, addParamsToUrl, getFullPath } from '../utils/url';

export const ACCESS_TOKEN_KEY = 'access_token';
export const ID_TOKEN_KEY = 'id_token';
export const STATE_KEY = 'auth0_state';
export const AUTH_CLIENT_ID_KEY = 'auth_client_id';
export const LOCATION_ID_KEY = 'curb_location_id';
export const PLATFORM_KEY = 'platform';
export const IMPERSONATED_KEY = 'impersonated';
export const TOKEN_EXPIRATION_KEY = 'token_expiration';
export const AUDIENCE_KEY = 'auth_audience';
export const DOMAIN_KEY = 'auth_domain';

export default class AuthService {
  constructor(params={}){
    const { clientId, domain, audience, nonce, useCordovaAuth, connection='Users' } = params;
    this.clientId = window.localStorage.getItem(AUTH_CLIENT_ID_KEY) || clientId;
    this.domain = window.localStorage.getItem(DOMAIN_KEY) || domain;
    this.audience = window.localStorage.getItem(AUDIENCE_KEY) || audience;
    this.nonce = nonce;
    this.useCordovaAuth = useCordovaAuth;
    this.connection = connection;
    this.APICall = getAPI(this);
    this.renewAuth = this.renewAuth.bind(this);
    this.logoutWithReentry = this.logoutWithReentry.bind(this);
    this.logout = this.logout.bind(this);
    if(this.domain && this.clientId){
      this.update();
      this.maintainAuthSession();
    }
  }

  static callbackURL(callbackPath='/token'){
    if(/^https?:\/\//.test(callbackPath)){
      return callbackPath;
    }
    // create url, clear search and hash and set callback path
    var url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.pathname = callbackPath;
    return url.toString();
  }

  maintainAuthSession(){
    if(!this.useCordovaAuth){
      let backgroundRenewAuth = () => this.renewAuth()
        .catch(
          // this can fail silently
          err => err
        );
      let expiration = parseInt(window.localStorage.getItem(TOKEN_EXPIRATION_KEY), 10);

      window.addEventListener(
        'beforeunload',
        e => {
          backgroundRenewAuth();
        }
      );

      // if we have a token and it will expire in less than 30 minutes, go ahead and renew it
      if(
        !isNaN(expiration) &&
        (expiration - Math.floor(Date.now() / 1000) < 60 * 30)
      ) {
        backgroundRenewAuth();
      }

    }
  }

  update(){
    if(!this.useCordovaAuth && this.clientId && this.domain){
      this.auth0 = new WebAuth(
        this.getAuthParams()
      );
    }
  }
  getRedirectURIWithEntry(entry, redirectUri=AuthService.callbackURL()){
    const url = new URL(redirectUri);
    url.searchParams.set('entry', entry);
    return url.toString();
  }
  authorize(params={}){
    return this.auth0.authorize(params);
  }
  getAuthParams(){
    const params = {
      domain: this.domain,
      clientID: this.clientId,
      audience: this.audience,
      scope: 'openid email profile',
      responseType: 'token',
      redirectUri: AuthService.callbackURL(),
      connection: this.connection
    };
    const state = window.localStorage.getItem(STATE_KEY);
    if(state){
      params.state = state;
    }
    return params;
  }
  isImpersonated(){
    return localStorage.getItem(IMPERSONATED_KEY) === 'true';
  }
  processAuthResult(authResult){
    const { access_token, id_token, state } = authResult;
    this.clearStoredAuthValues();
    if(access_token){
      localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
      // look for a client id in the token, save it to persistent storage and reupdate
      try {
        const tokenDecoded = jwtDecode(access_token);
        const { azp, exp, aud, iss } = tokenDecoded;
        if(azp){
          this.clientId = azp;
          window.localStorage.setItem(AUTH_CLIENT_ID_KEY, this.clientId);
        }
        if(exp){
          window.localStorage.setItem(TOKEN_EXPIRATION_KEY, exp);
        }
        if(aud){
          let audience = Array.isArray(aud)
            ? aud[0]
            : aud;
          this.audience = audience;
          window.localStorage.setItem(AUDIENCE_KEY, this.audience);
        }
        if(iss){
          this.domain = iss.replace(/(^https?:\/\/)|(\/$)/ig, '');
          window.localStorage.setItem(DOMAIN_KEY, this.domain);
        }
      }
      catch(err){
        console.error(err);
      }
    }
    if(id_token){
      localStorage.setItem(ID_TOKEN_KEY, id_token)
    }
    if(state){
      localStorage.setItem(STATE_KEY, state);
    }
    this.update();
  }
  processAuthHash(hash){
    // uses auth0 parseHash method to extract data from url hash
    if(!hash){
      throw new Error('Auth hash is required');
    }
    hash = hash.replace(/^#/, '');
    const authResult = parseQueryString(hash);
    if(authResult){
      this.processAuthResult(authResult);
    }
    return authResult;
  }
  getAuthHash(){
    const tokenKeys = [ACCESS_TOKEN_KEY];
    // send an id token if there is one
    if(this.getToken(ID_TOKEN_KEY)){
      tokenKeys.push(ID_TOKEN_KEY);
    }
    return tokenKeys.reduce(
      (a,b,i) => {
        return [
          a,
          i === 0 ? '?' : '&',
          b,
          '=',
          localStorage.getItem(b)
        ].join('');
      },
      ''
    );
  }
  renewAuth(params={}){
    return new Promise(
      (resolve, reject) => {
        if(!this.loggedIn()){
          return reject('Can not renew auth - user is not logged in');
        }
        if(this.isImpersonated()){
          return reject('Impersonation session can not renew authentication');
        }
        const state = localStorage.getItem(STATE_KEY);
        const extraParams = state
          ? {
            state: localStorage.getItem(STATE_KEY),
            nonce: this.nonce
          }
          : {};

        this.auth0.checkSession(
          {
            timeout: 10000,
            ...this.getAuthParams(),
            ...extraParams,
          },
          (err, result) => {
             if (err) {
               console.error(`Could not get a new token using silent authentication (${err.error}).`);
               reject(err);
               // only autoLogout
             } else {
               this.processAuthResult({
                 access_token: result.accessToken,
                 state: result.state
               });
               resolve(result);
             }
           }
         );
      }
    );
  }
  loggedIn(){
    return !!this.getToken();
  }
  setToken(token, key=ACCESS_TOKEN_KEY){
    if(token) {
      localStorage.setItem(key, token);
    }
  }
  getToken(key=ACCESS_TOKEN_KEY){
    const token = localStorage.getItem(key);
    return token && token === 'null' ? null : token;
  }
  clearStoredAuthValues(){
    [
      ACCESS_TOKEN_KEY,
      ID_TOKEN_KEY,
      IMPERSONATED_KEY,
      LOCATION_ID_KEY,
      STATE_KEY,
      AUTH_CLIENT_ID_KEY,
      TOKEN_EXPIRATION_KEY,
      AUDIENCE_KEY,
      DOMAIN_KEY
    ]
    .forEach(key => localStorage.removeItem(key));
  }
  logoutWithReentry(entry=getFullPath()){
    return this.logout({
      entry
    });
  }
  logout({ entry, callbackPath='/login', omitReturnToParams=false, returnToParams={} }={}){
    let returnTo = AuthService.callbackURL(callbackPath);
    if(!omitReturnToParams){
      returnToParams.client_id = this.clientId;
      let redirect_uri_params = {};
      if(entry){
        redirect_uri_params.entry = encodeURIComponent(entry);
      }
      returnToParams.redirect_uri = addParamsToUrl(redirect_uri_params, AuthService.callbackURL()).toString();
    };
    returnTo = `${returnTo}${toQueryString(returnToParams)}`;
    this.clearStoredAuthValues();

    // window.location = returnTo;

    if(this.auth0){
      this.auth0.logout({
        clientID: this.clientId,
        returnTo
      });
    }
  }
}
